import React from 'react';
import { flag } from '../config';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail } from 'lucide-react';
import NewsletterSignup from './NewsletterSignup';

// Custom styles for the new design schema
const footerStyles = `
  .footer-font-display { font-family: 'Playfair Display', serif; }
  .footer-font-body { font-family: 'DM Sans', sans-serif; }
`;

/**
 * The pre-redesign footer, unchanged: components/Footer.jsx renders it while SLOT_MODEL is
 * off (its markup is pinned against the base commits by phase5c.routes.qa §1b). With
 * SLOT_MODEL on the redesigned footer (components/site/footer/SiteFooter) replaces it.
 */
const FooterLegacy = () => {
  return (
    <>
      <style>{footerStyles}</style>

      {/* Fonts are loaded once from public/index.html (preconnect + stylesheet). */}

      <footer className="footer-font-body" style={{ backgroundColor: '#FEF7ED' }}>
        {/* Newsletter Section — hidden with EXTRA_FORMS off so the band isn't empty */}
        {flag('EXTRA_FORMS') && (
        <div className="relative overflow-hidden" style={{
          borderTop: '4px solid',
          borderImage: 'linear-gradient(to right, #6F5BEB, #FF7A1A, #F59E0B) 1'
        }}>
          <div className="absolute inset-0">
            <img src="https://iolab.co/wp-content/uploads/2025/12/EMAIL-BG-3.png" alt="" className="w-full h-full object-cover opacity-15" />
          </div>
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(254, 247, 237, 0.9)' }}></div>

          <div className="max-w-[90rem] mx-auto px-6 py-20 relative z-10">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="footer-font-display text-3xl md:text-4xl mb-4" style={{ color: '#1e1b4b' }}>
                Get Weekly Local Deals
              </h3>
              <p className="text-xl mb-8" style={{ color: 'rgba(30, 27, 75, 0.6)' }}>
                Subscribe to our newsletter for curated weekly deals from local businesses in your area.
              </p>
              <div className="flex justify-center">
                <NewsletterSignup variant="default" />
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Footer Links */}
        <div className="max-w-[90rem] mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            {/* Brand Column */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="/tappd-logo.png" 
                  alt="Tappd" 
                  className="h-12 w-auto object-contain"
                />
              </div>
              <p className="mb-6" style={{ color: 'rgba(30, 27, 75, 0.6)' }}>
                QR-enabled coaster ads that turn bar seats into local customers.
              </p>
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/tappd.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Tappd on Instagram"
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-colors duration-200 group"
                  style={{ backgroundColor: 'rgba(30, 27, 75, 0.1)' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FF7A1A'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(30, 27, 75, 0.1)'}
                >
                  <Instagram size={20} className="text-[#1e1b4b] group-hover:text-white" />
                </a>
                <a
                  href="https://facebook.com/tappdapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Tappd on Facebook"
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-colors duration-200 group"
                  style={{ backgroundColor: 'rgba(30, 27, 75, 0.1)' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FF7A1A'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(30, 27, 75, 0.1)'}
                >
                  <Facebook size={20} className="text-[#1e1b4b] group-hover:text-white" />
                </a>
              </div>
            </div>

            {/* Links Column */}
            <div>
              <h4 className="font-semibold mb-6" style={{ color: '#1e1b4b' }}>
                Quick Links
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/how-it-works"
                    className="hover:text-orange-500 transition-colors duration-200"
                    style={{ color: 'rgba(30, 27, 75, 0.6)' }}
                  >
                    How It Works
                  </Link>
                </li>
                <li>
                  <Link
                    to="/our-story"
                    className="hover:text-orange-500 transition-colors duration-200"
                    style={{ color: 'rgba(30, 27, 75, 0.6)' }}
                  >
                    Our Story
                  </Link>
                </li>
                <li>
                  <Link
                    to="/for-businesses"
                    className="hover:text-orange-500 transition-colors duration-200"
                    style={{ color: 'rgba(30, 27, 75, 0.6)' }}
                  >
                    For Businesses
                  </Link>
                </li>
                <li>
                  <Link
                    to="/for-venues"
                    className="hover:text-orange-500 transition-colors duration-200"
                    style={{ color: 'rgba(30, 27, 75, 0.6)' }}
                  >
                    For Venues
                  </Link>
                </li>
                <li>
                  <Link
                    to="/pricing"
                    className="hover:text-orange-500 transition-colors duration-200"
                    style={{ color: 'rgba(30, 27, 75, 0.6)' }}
                  >
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-orange-500 transition-colors duration-200"
                    style={{ color: 'rgba(30, 27, 75, 0.6)' }}
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-orange-500 transition-colors duration-200"
                    style={{ color: 'rgba(30, 27, 75, 0.6)' }}
                  >
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Column */}
            <div>
              <h4 className="font-semibold mb-6" style={{ color: '#1e1b4b' }}>
                Contact
              </h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="mailto:hello@tappd.app"
                    className="hover:text-orange-500 transition-colors duration-200 flex items-center gap-2"
                    style={{ color: 'rgba(30, 27, 75, 0.6)' }}
                  >
                    <Mail size={16} />
                    hello@tappd.app
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-8 text-center" style={{ borderTop: '1px solid rgba(30, 27, 75, 0.1)' }}>
            <p className="text-sm mb-2" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>
              © {new Date().getFullYear()} Tappd App. All rights reserved.
            </p>
            <p className="text-sm" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>
              Developed by{' '}
              <a 
                href="https://iolab.co" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-orange-500 transition-colors duration-200"
                style={{ color: 'rgba(30, 27, 75, 0.6)' }}
              >
                iOLab Digital
              </a>
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default FooterLegacy;
